import { useState } from "react";
import { Lock, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function ChangePasswordCard() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [ok, setOk] = useState(false);

    const canSubmit = current.length >= 1 && next.length >= 6 && next === confirm && !busy;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated", { description: "Use your new password next time you sign in." });
            setCurrent("");
            setNext("");
            setConfirm("");
            setOk(true);
            setTimeout(() => setOk(false), 3500);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(formatApiError(detail) || "Could not update your password.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="card-rw p-7 mb-6" data-testid="change-password-card">
            <div className="flex items-start gap-5 mb-4">
                <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center flex-shrink-0">
                    <Lock className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-[220px]">
                    <h2 className="text-xl font-medium text-[#1C2321] mb-1" style={{ fontFamily: "Outfit" }}>
                        Change password
                    </h2>
                    <p className="text-[#515B57]">
                        Update the password you use to sign in. Minimum six characters.
                    </p>
                </div>
            </div>

            <form onSubmit={submit} className="grid gap-4 sm:max-w-md pt-2">
                <label className="text-sm text-[#1C2321]">
                    <span className="label-eyebrow block mb-1">Current password</span>
                    <input
                        type="password"
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        autoComplete="current-password"
                        className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7E2] focus:outline-none focus:border-[#2D5D4E] bg-white"
                        data-testid="change-password-current"
                        required
                    />
                </label>
                <label className="text-sm text-[#1C2321]">
                    <span className="label-eyebrow block mb-1">New password</span>
                    <input
                        type="password"
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        autoComplete="new-password"
                        minLength={6}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7E2] focus:outline-none focus:border-[#2D5D4E] bg-white"
                        data-testid="change-password-new"
                        required
                    />
                </label>
                <label className="text-sm text-[#1C2321]">
                    <span className="label-eyebrow block mb-1">Confirm new password</span>
                    <input
                        type="password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        autoComplete="new-password"
                        minLength={6}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7E2] focus:outline-none focus:border-[#2D5D4E] bg-white"
                        data-testid="change-password-confirm"
                        required
                    />
                    {confirm.length > 0 && next !== confirm && (
                        <span className="block mt-1 text-xs text-[#B86644]" data-testid="change-password-mismatch">
                            Passwords don't match yet.
                        </span>
                    )}
                </label>
                <button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid="change-password-submit"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237] disabled:opacity-50 disabled:cursor-not-allowed w-fit"
                >
                    {ok ? (
                        <>
                            <Check className="w-4 h-4" /> Saved
                        </>
                    ) : busy ? (
                        "Saving…"
                    ) : (
                        "Update password"
                    )}
                </button>
            </form>
        </section>
    );
}
